import { createFileRoute, redirect, useNavigate } from '@tanstack/react-router';
import { preloadListingFixtures } from '~portal/data/fixtures';
import { isPageTurn, isStandalone, logger } from '~portal/lib';
import { type OperatorsSearchPatch, parseOperatorsSearch, withOperatorsSearch } from './-listing';
import { OperatorsListingPage } from './-listing-page';

/**
 * @desc The operators listing. Tab, view, sort, items per page, page, search and every filter
 * live in the URL (`parseOperatorsSearch`), so a filtered view can be shared or bookmarked; the
 * page body is `OperatorsListingPage`. Platform-only: the standalone build is the chat app alone,
 * so it redirects to a new chat like the other platform pages.
 */
export const Route = createFileRoute('/_protected/operators/')({
  beforeLoad: () => {
    if (isStandalone) {
      throw redirect({ to: '/chat/$conversationId', params: { conversationId: 'new' } });
    }
  },
  // The operators and regions fixtures are lazy chunks: fetch them alongside this route's own.
  loader: preloadListingFixtures,
  validateSearch: parseOperatorsSearch,
  component: OperatorsPage,
});

function OperatorsPage() {
  const search = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });

  const update = (patch: OperatorsSearchPatch) => {
    navigate({
      search: (prev) => withOperatorsSearch(prev, patch),
      // A page turn starts at the top; a filter, sort or search keeps the reader in place.
      resetScroll: isPageTurn(patch),
    }).catch((err) => {
      logger.error('[Operators] Navigation failed', {
        error: err instanceof Error ? err.message : String(err),
      });
    });
  };

  return <OperatorsListingPage search={search} onSearch={update} />;
}
